import React, { useState, useMemo } from 'react';
import {
  Book,
  Search,
  Edit,
  Plus,
  Trash2,
  Filter,
  Globe,
  FileText,
  AlertCircle
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Policy Regulation Interface
interface PolicyRegulation {
  id: string;
  title: string;
  category: 'data_protection' | 'business' | 'tax' | 'employment' | 'privacy';
  jurisdiction: {
    country: string;
    region?: string;
  };
  description: string;
  effectiveDate: string;
  expirationDate?: string;
  applicabilityStatus: 'active' | 'pending' | 'expired';
  complianceRequirements: string[];
  tags: string[];
  documentUrl?: string;
}

const PolicyManagementPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'General Settings', href: '#' },
    { label: 'Policy Management' }
  ]);
  // Initial Policy Regulations
  const [policies, setPolicies] = useState<PolicyRegulation[]>([
    {
      id: 'policy001',
      title: 'California Consumer Privacy Act (CCPA)',
      category: 'data_protection',
      jurisdiction: {
        country: 'United States',
        region: 'California'
      },
      description: 'Provides California residents with specific rights regarding their personal information',
      effectiveDate: '2020-01-01',
      applicabilityStatus: 'active',
      complianceRequirements: [
        'Provide data access rights',
        'Allow opt-out of data sales',
        'Implement data protection measures'
      ],
      tags: ['privacy', 'consumer rights', 'data protection'],
      documentUrl: 'https://example.com/ccpa-document'
    },
    {
      id: 'policy002',
      title: 'General Data Protection Regulation (GDPR)',
      category: 'data_protection',
      jurisdiction: {
        country: 'European Union'
      },
      description: 'Regulation on data protection and privacy in the European Union',
      effectiveDate: '2018-05-25',
      applicabilityStatus: 'active',
      complianceRequirements: [
        'Obtain explicit consent',
        'Implement data protection officer',
        'Provide data portability'
      ],
      tags: ['privacy', 'european union', 'data protection'],
      documentUrl: 'https://example.com/gdpr-document'
    }
  ]);

  // Filters State
  const [filters, setFilters] = useState({
    searchTerm: '',
    category: '',
    status: '',
    jurisdiction: ''
  });

  // State for Policy Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState<Partial<PolicyRegulation>>({
    jurisdiction: { country: '' } // Initialize with empty country
  });
  const [isEditMode, setIsEditMode] = useState(false);

  // Filtered Policies
  const filteredPolicies = useMemo(() => {
    return policies.filter(policy =>
      (filters.searchTerm === '' ||
        policy.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        policy.description.toLowerCase().includes(filters.searchTerm.toLowerCase())) &&
      (filters.category === '' || policy.category === filters.category) &&
      (filters.status === '' || policy.applicabilityStatus === filters.status) &&
      (filters.jurisdiction === '' ||
        policy.jurisdiction.country.toLowerCase().includes(filters.jurisdiction.toLowerCase()) ||
        policy.jurisdiction.region?.toLowerCase().includes(filters.jurisdiction.toLowerCase()))
    );
  }, [policies, filters]);

  // Open Add Policy Modal
  const handleAddPolicy = () => {
    setCurrentPolicy({
      jurisdiction: { country: '' } // Initialize with empty country
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Open Edit Policy Modal
  const handleEditPolicy = (policy: PolicyRegulation) => {
    setCurrentPolicy({ ...policy });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Save Policy (Add or Edit)
  const handleSavePolicy = () => {
    // Validate required fields
    if (!currentPolicy.title || !currentPolicy.category || !currentPolicy.jurisdiction?.country) {
      alert('Please fill in all required fields');
      return;
    }

    const newPolicy: PolicyRegulation = {
      id: currentPolicy.id || `policy${policies.length + 1}`,
      title: currentPolicy.title,
      category: currentPolicy.category as PolicyRegulation['category'],
      jurisdiction: {
        country: currentPolicy.jurisdiction.country,
        region: currentPolicy.jurisdiction.region
      },
      description: currentPolicy.description || '',
      effectiveDate: currentPolicy.effectiveDate || new Date().toISOString().split('T')[0],
      expirationDate: currentPolicy.expirationDate,
      applicabilityStatus: currentPolicy.applicabilityStatus || 'pending' as PolicyRegulation['applicabilityStatus'],
      complianceRequirements: currentPolicy.complianceRequirements || [],
      tags: currentPolicy.tags || [],
      documentUrl: currentPolicy.documentUrl
    };

    if (isEditMode) {
      // Update existing policy
      setPolicies(policies.map(policy =>
        policy.id === newPolicy.id ? newPolicy : policy
      ));
    } else {
      // Add new policy
      setPolicies([...policies, newPolicy]);
    }

    // Close modal
    setIsModalOpen(false);
    setCurrentPolicy({
      jurisdiction: { country: '' } // Reset with empty country
    });
  };

  // Delete Policy
  const handleDeletePolicy = (id: string) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      setPolicies(policies.filter(policy => policy.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Book className="h-7 w-7 mr-3 text-purple-600" />
              Policy Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and track regulatory policies across jurisdictions
            </p>
          </div>
          <button
            onClick={handleAddPolicy}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add New Policy
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search policies..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            />
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          </div>

          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All Categories</option>
            <option value="data_protection">Data Protection</option>
            <option value="business">Business</option>
            <option value="tax">Tax</option>
            <option value="employment">Employment</option>
            <option value="privacy">Privacy</option>
          </select>

          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
          </select>

          <input
            type="text"
            placeholder="Filter by Jurisdiction"
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={filters.jurisdiction}
            onChange={(e) => setFilters({ ...filters, jurisdiction: e.target.value })}
          />
        </div>

        {/* Policies Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jurisdiction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effective Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPolicies.map((policy) => (
                <tr key={policy.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-3 text-blue-500" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {policy.title}
                        </div>
                        {policy.tags && (
                          <div className="text-xs text-gray-500 mt-1 space-x-1">
                            {policy.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {policy.jurisdiction.country}
                      {policy.jurisdiction.region && ` (${policy.jurisdiction.region})`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 capitalize">
                      {policy.category.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(policy.effectiveDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`
                      px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${policy.applicabilityStatus === 'active' ? 'bg-green-100 text-green-800' :
                        policy.applicabilityStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                      }
                    `}>
                      {policy.applicabilityStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEditPolicy(policy)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id)}
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Policy Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-[800px] shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {isEditMode ? 'Edit Policy' : 'Add New Policy'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <AlertCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Title
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.title || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, title: e.target.value })}
                    placeholder="Enter policy title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.category || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, category: e.target.value as PolicyRegulation['category'] })}
                  >
                    <option value="">Select Category</option>
                    <option value="data_protection">Data Protection</option>
                    <option value="business">Business</option>
                    <option value="tax">Tax</option>
                    <option value="employment">Employment</option>
                    <option value="privacy">Privacy</option>
                  </select>
                </div>

                {/* Jurisdiction */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.jurisdiction?.country || ''}
                    onChange={(e) => setCurrentPolicy({
                      ...currentPolicy,
                      jurisdiction: {
                        ...currentPolicy.jurisdiction,
                        country: e.target.value // Always assign the value, even if empty
                      }
                    })}
                    placeholder="Enter country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.jurisdiction?.region || ''}
                    onChange={(e) => setCurrentPolicy({
                      ...currentPolicy,
                      jurisdiction: {
                        country: currentPolicy.jurisdiction?.country || '', // Ensure country is always set
                        region: e.target.value
                      }
                    })}
                    placeholder="Enter region (optional)"
                  />
                </div>

                {/* Dates */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effective Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.effectiveDate || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, effectiveDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date (Optional)
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.expirationDate || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, expirationDate: e.target.value })}
                  />
                </div>

                {/* Status and Document */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applicability Status
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.applicabilityStatus || 'pending'}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, applicabilityStatus: e.target.value as PolicyRegulation['applicabilityStatus'] })}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document URL (Optional)
                  </label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.documentUrl || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, documentUrl: e.target.value })}
                    placeholder="Enter document URL"
                  />
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={currentPolicy.description || ''}
                    onChange={(e) => setCurrentPolicy({ ...currentPolicy, description: e.target.value })}
                    placeholder="Enter policy description"
                    rows={4}
                  />
                </div>

                {/* Compliance Requirements */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Compliance Requirements
                  </label>
                  <div className="space-y-2">
                    {(currentPolicy.complianceRequirements || []).map((req, index) => (
                      <div key={index} className="flex items-center">
                        <input
                          type="text"
                          className="flex-grow px-3 py-2 border border-gray-300 rounded-md mr-2"
                          value={req}
                          onChange={(e) => {
                            const newReqs = [...(currentPolicy.complianceRequirements || [])];
                            newReqs[index] = e.target.value;
                            setCurrentPolicy({ ...currentPolicy, complianceRequirements: newReqs });
                          }}
                          placeholder="Enter compliance requirement"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newReqs = (currentPolicy.complianceRequirements || []).filter((_, i) => i !== index);
                            setCurrentPolicy({ ...currentPolicy, complianceRequirements: newReqs });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newReqs = [...(currentPolicy.complianceRequirements || []), ''];
                        setCurrentPolicy({ ...currentPolicy, complianceRequirements: newReqs });
                      }}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Add Requirement
                    </button>
                  </div>
                </div>

                {/* Tags */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 items-center">
                    {(currentPolicy.tags || []).map((tag, index) => (
                      <div key={index} className="flex items-center bg-blue-100 px-2 py-1 rounded-full">
                        <span className="text-sm text-blue-800 mr-2">{tag}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newTags = (currentPolicy.tags || []).filter((_, i) => i !== index);
                            setCurrentPolicy({ ...currentPolicy, tags: newTags });
                          }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <input
                      type="text"
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Add new tag"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                          const newTag = (e.target as HTMLInputElement).value.trim();
                          const newTags = [...(currentPolicy.tags || []), newTag];
                          setCurrentPolicy({ ...currentPolicy, tags: newTags });
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePolicy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {isEditMode ? 'Update Policy' : 'Add Policy'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyManagementPage;